import { Controller } from "@hotwired/stimulus"
import { deserializeFromLocalStorage, serializeToLocalStorage } from "~/helpers/local_storage_helpers"
import { isPresent, Optional } from "~/types/types.util"
import { getBsTriggerElements } from "~/helpers/boostrap_helpers"

const STORAGE_NAMESPACE = "nuggit_collapse_states"
const SELECTOR_DATA_TOGGLE = "[data-bs-toggle=\"collapse\"]"

// A mapping of element IDs to their collapse state (true for shown, false for hidden)
type CollapseState = Record<string, boolean>

export default class extends Controller<HTMLElement> {
  // Use the element's ID as the key to store its collapse state
  get elementStorageKey(): string {
    return this.element.id
  }

  // Find the control elements that potentially toggles this collapse element
  get toggleControls() {
    return getBsTriggerElements(this.element, SELECTOR_DATA_TOGGLE)
  }

  // Retrieve the current collapse state from local storage
  get collapseState(): Optional<CollapseState> {
    return deserializeFromLocalStorage<CollapseState>(STORAGE_NAMESPACE)
  }

  connect() {
    this.#restoreInitialState()
    this.element.addEventListener("show.bs.collapse", this.#saveShowState)
    this.element.addEventListener("hide.bs.collapse", this.#saveHideState)
  }

  disconnect() {
    this.element.removeEventListener("show.bs.collapse", this.#saveShowState)
    this.element.removeEventListener("hide.bs.collapse", this.#saveHideState)
  }

  #saveShowState = () => this.#saveState(true)
  #saveHideState = () => this.#saveState(false)
  #saveState = (isShown: boolean) => {
    const newState: CollapseState = this.collapseState ?? {}
    newState[this.elementStorageKey] = isShown
    serializeToLocalStorage(STORAGE_NAMESPACE, newState)
  }

  #restoreInitialState() {
    if (isPresent(this.collapseState)) {
      const isShown = this.collapseState[this.elementStorageKey]
      if (isShown) {
        this.element.classList.add("show")
        this.toggleControls.forEach((control) => {
          control.setAttribute("aria-expanded", "true")
        })
      } else {
        this.element.classList.remove("show")
        this.toggleControls.forEach((control) => {
          control.setAttribute("aria-expanded", "false")
        })
      }
    }
  }
}
